import React, { FC, ReactNode, useEffect } from 'react'
import { useSocketNotifications } from '../hooks/useSocketNotifications'
import useAuth from '../hooks/useAuth'
import { useActiveConversation } from '../contexts/ActiveConversationContext'
import useNotifications from '../hooks/useNotifications'

interface SocketNotificationProviderProps {
  children: ReactNode
}

/**
 * Provider that initializes Socket.IO for real-time notifications.
 * See docs/FRONTEND-SOCKET-IO-CORS.md: serverUrl must match REST API base URL; JWT in auth.token.
 * Used in authenticated layout only.
 */
const SocketNotificationProvider: FC<SocketNotificationProviderProps> = ({ children }) => {
  const { me } = useAuth()
  const { isConversationViewed } = useActiveConversation()
  const { fetchNotifications } = useNotifications()
  const fetchNotificationsRef = React.useRef(fetchNotifications)
  const lastSocketFetchRef = React.useRef<number>(0)
  const SOCKET_FETCH_DEBOUNCE_MS = 3000 // Minimum 3 seconds between socket-triggered fetches

  // Update ref when fetchNotifications changes
  React.useEffect(() => {
    fetchNotificationsRef.current = fetchNotifications
  }, [fetchNotifications])

  // Same host as REST API (REACT_APP_API_URL); token key should match REST (e.g. maarefatiAuthToken)
  const serverUrl = process.env.REACT_APP_API_URL
  const authTokenKey = process.env.REACT_APP_AUTH_TOKEN_KEY || 'auth_token'
  const token = localStorage.getItem(authTokenKey)

  // Initialize socket connection with chat notification suppression
  useSocketNotifications(serverUrl, token || undefined, {
    enabled: !!me && !!token && !!serverUrl, // Only enable when user is logged in
    isConversationViewed, // Suppress browser notifications when viewing that conversation
    onNotification: (notification) => {
      // Refresh notifications list when a new notification arrives via Socket.IO
      // This ensures the API response includes the latest notifications
      // But debounce to prevent infinite loops
      const userId = me?._id
      if (userId) {
        const now = Date.now()
        if (now - lastSocketFetchRef.current < SOCKET_FETCH_DEBOUNCE_MS) {
          // Skip if we just fetched recently (prevent infinite loop)
          if (process.env.NODE_ENV === 'development') {
            console.debug('[notifications] Skipping socket-triggered fetch (debounced)')
          }
          return
        }
        lastSocketFetchRef.current = now
        
        // Small delay to allow backend to persist the notification
        setTimeout(() => {
          fetchNotificationsRef.current({ recipient: userId }).catch((err) => {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[notifications] Failed to refresh after Socket.IO notification:', err)
            }
          })
        }, 500)
      }
    },
  })

  // Request browser notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission()
    }
  }, [])

  return <>{children}</>
}

export default SocketNotificationProvider
